import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Check, ChevronLeft, X } from 'lucide-react';
import { cn } from '../lib/cn';

// ---------------------------------------------------------------- PageHeader
export interface PageHeaderProps {
  title: ReactNode;
  /** Palavra ou expressão em Instrument Serif itálico depois do título (acento editorial). */
  accent?: ReactNode;
  description?: ReactNode;
  /** Rótulo pequeno acima do título (Geist Mono em caixa alta). */
  eyebrow?: ReactNode;
  /** Botões à direita (no celular vão para baixo do título). */
  actions?: ReactNode;
  /** Link "voltar": rota em href (link comum) ou ação (navegação do roteador). */
  backHref?: string;
  onBack?: () => void;
  backLabel?: string;
  size?: 'md' | 'lg';
  className?: string;
}

/** Cabeçalho de página: voltar, rótulo, título com acento opcional, descrição e ações. */
export function PageHeader({
  title,
  accent,
  description,
  eyebrow,
  actions,
  backHref,
  onBack,
  backLabel = 'Voltar',
  size = 'md',
  className,
}: PageHeaderProps) {
  const backClass =
    'mb-3 -ml-1.5 inline-flex h-8 items-center gap-1 rounded-lg px-1.5 text-sm font-medium text-ink-2 transition-colors hover:bg-ink/[0.05] hover:text-ink';
  return (
    <header className={cn('flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between', className)}>
      <div className="min-w-0">
        {backHref ? (
          <a href={backHref} className={backClass}>
            <ChevronLeft className="size-4" aria-hidden="true" />
            {backLabel}
          </a>
        ) : onBack ? (
          <button type="button" onClick={onBack} className={backClass}>
            <ChevronLeft className="size-4" aria-hidden="true" />
            {backLabel}
          </button>
        ) : null}
        {eyebrow ? <div className="eyebrow mb-2 text-ink-3">{eyebrow}</div> : null}
        <h1 className={cn('text-balance text-ink', size === 'lg' ? 'text-[32px] font-[650] leading-[1.08] tracking-[-0.035em] sm:text-display' : 'text-title')}>
          {title}
          {accent ? (
            <>
              {' '}
              <span className="serif-accent text-primary-strong">{accent}</span>
            </>
          ) : null}
        </h1>
        {description ? <p className="mt-2 max-w-2xl text-[15px] leading-relaxed text-ink-2">{description}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
    </header>
  );
}

// ---------------------------------------------------------------- Chip
export const chipVariants = cva(
  'inline-flex select-none items-center gap-1.5 whitespace-nowrap rounded-full font-medium transition-[background-color,color,box-shadow,scale] duration-150 ease-standard [&_svg]:shrink-0',
  {
    variants: {
      tone: {
        neutral: 'bg-surface text-ink-2 ring-1 ring-inset ring-line-strong',
        primary: 'bg-primary-soft text-primary-strong',
        success: 'bg-success-soft text-success',
        warning: 'bg-warning-soft text-warning',
        danger: 'bg-danger-soft text-danger',
        info: 'bg-info-soft text-info',
        accent: 'bg-accent text-on-accent',
      },
      size: {
        sm: 'h-7 px-2.5 text-xs [&_svg]:size-3.5',
        md: 'h-8 px-3 text-[13px] [&_svg]:size-4',
      },
      selected: {
        true: 'bg-primary-soft text-primary-strong ring-1 ring-inset ring-primary/45',
      },
    },
    defaultVariants: { tone: 'neutral', size: 'md' },
  },
);

export interface ChipProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'>, Omit<VariantProps<typeof chipVariants>, 'selected'> {
  selected?: boolean;
  /** Com onClick vira um botão de alternar (aria-pressed). */
  onClick?: () => void;
  /** Mostra um "x" para remover (filtros aplicados). */
  onRemove?: () => void;
  removeLabel?: string;
  icon?: ReactNode;
}

/** Etiqueta compacta: filtro alternável, tag ou filtro aplicado com "x". */
export function Chip({ children, selected, onClick, onRemove, removeLabel = 'Remover', icon, tone, size, className, disabled, type = 'button', ...rest }: ChipProps) {
  const classes = cn(
    chipVariants({ tone, size, selected: selected || undefined }),
    onClick && !selected && tone === undefined && 'hover:text-ink hover:ring-ink/25',
    onClick && 'active:scale-[0.96] motion-reduce:active:scale-100',
    disabled && 'pointer-events-none opacity-50',
    onRemove && 'pr-1',
    className,
  );
  const content = (
    <>
      {selected && !icon ? <Check aria-hidden="true" strokeWidth={2.5} /> : icon}
      {children}
    </>
  );
  const remove = onRemove ? (
    <button
      type="button"
      onClick={onRemove}
      aria-label={removeLabel}
      className="ml-0.5 flex size-5 items-center justify-center rounded-full text-current opacity-70 transition-[opacity,background-color] hover:bg-ink/10 hover:opacity-100"
    >
      <X className="size-3.5" />
    </button>
  ) : null;
  if (onClick && !onRemove) {
    return (
      <button type={type} aria-pressed={selected ?? false} onClick={onClick} disabled={disabled} className={classes} {...rest}>
        {content}
      </button>
    );
  }
  return (
    <span className={classes}>
      {onClick ? (
        <button type="button" aria-pressed={selected ?? false} onClick={onClick} disabled={disabled} className="inline-flex items-center gap-1.5">
          {content}
        </button>
      ) : (
        content
      )}
      {remove}
    </span>
  );
}

// ---------------------------------------------------------------- Stepper
export interface StepperStep {
  label: ReactNode;
  description?: ReactNode;
}

/** Etapas horizontais (checkout, assistentes). Etapas concluídas podem ser clicáveis para voltar. */
export function Stepper({
  steps,
  current,
  onStepClick,
  className,
}: {
  steps: (StepperStep | string)[];
  /** Índice da etapa atual (0 = primeira). */
  current: number;
  onStepClick?: (index: number) => void;
  className?: string;
}) {
  const list = steps.map((s) => (typeof s === 'string' ? { label: s } : s));
  return (
    <ol className={cn('flex w-full items-start', className)}>
      {list.map((step, i) => {
        const state = i < current ? 'done' : i === current ? 'current' : 'todo';
        const clickable = Boolean(onStepClick) && state === 'done';
        const marker = (
          <span
            className={cn(
              'relative z-10 flex size-7 items-center justify-center rounded-full text-[13px] font-semibold transition-[background-color,color,box-shadow] duration-300 ease-standard',
              state === 'done' && 'bg-primary text-on-primary',
              state === 'current' && 'bg-surface text-primary-strong shadow-[0_0_0_2px_var(--color-primary),0_0_0_6px_color-mix(in_oklab,var(--color-primary)_16%,transparent)]',
              state === 'todo' && 'bg-surface text-ink-3 ring-1 ring-inset ring-line-strong',
            )}
          >
            {state === 'done' ? <Check className="size-4" strokeWidth={3} aria-hidden="true" /> : <span className="tabular">{i + 1}</span>}
          </span>
        );
        const text = (
          <span className="mt-2 block px-1">
            <span className={cn('block text-[13px] font-medium leading-tight', state === 'todo' ? 'text-ink-3' : 'text-ink')}>{step.label}</span>
            {step.description ? <span className="mt-0.5 hidden text-xs text-ink-3 sm:block">{step.description}</span> : null}
          </span>
        );
        return (
          <li key={i} className="relative flex flex-1 flex-col items-center text-center" aria-current={state === 'current' ? 'step' : undefined}>
            {i > 0 ? (
              <span aria-hidden="true" className="absolute left-[-50%] right-1/2 top-[13px] h-0.5 overflow-hidden rounded-full bg-line-strong">
                <span
                  className={cn('block h-full origin-left bg-primary transition-transform duration-500 ease-snappy', i <= current ? 'scale-x-100' : 'scale-x-0')}
                />
              </span>
            ) : null}
            {clickable ? (
              <button type="button" onClick={() => onStepClick?.(i)} className="flex flex-col items-center rounded-xl">
                {marker}
                {text}
              </button>
            ) : (
              <>
                {marker}
                {text}
              </>
            )}
            <span className="sr-only">{state === 'done' ? '(concluída)' : state === 'current' ? '(etapa atual)' : ''}</span>
          </li>
        );
      })}
    </ol>
  );
}

// ---------------------------------------------------------------- Timeline
export interface TimelineItem {
  id?: string | number;
  title: ReactNode;
  description?: ReactNode;
  time?: ReactNode;
  icon?: ReactNode;
  tone?: 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'info';
}

const TIMELINE_TONES: Record<NonNullable<TimelineItem['tone']>, string> = {
  neutral: 'bg-surface-2 text-ink-3 ring-1 ring-inset ring-line',
  primary: 'bg-primary-soft text-primary-strong',
  success: 'bg-success-soft text-success',
  warning: 'bg-warning-soft text-warning',
  danger: 'bg-danger-soft text-danger',
  info: 'bg-info-soft text-info',
};

/** Linha do tempo vertical (histórico do pedido, atividades). */
export function Timeline({ items, className }: { items: TimelineItem[]; className?: string }) {
  return (
    <ol className={cn('relative', className)}>
      {items.map((it, i) => {
        const last = i === items.length - 1;
        return (
          <li key={it.id ?? i} className="relative flex gap-3 pb-5 last:pb-0">
            {!last ? <span aria-hidden="true" className="absolute bottom-0 left-[13.5px] top-8 w-px bg-line-strong" /> : null}
            <span
              className={cn(
                'relative flex size-7 shrink-0 items-center justify-center rounded-full [&_svg]:size-3.5',
                TIMELINE_TONES[it.tone ?? 'neutral'],
              )}
            >
              {it.icon ?? <span aria-hidden="true" className="size-1.5 rounded-full bg-current" />}
            </span>
            <div className="min-w-0 flex-1 pt-1">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                <span className="text-[14.5px] font-medium text-ink">{it.title}</span>
                {it.time ? <span className="tabular text-xs text-ink-3">{it.time}</span> : null}
              </div>
              {it.description ? <div className="mt-0.5 text-[13.5px] leading-relaxed text-ink-2">{it.description}</div> : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
